import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import core, { GQL } from '@gin-betting/core';
import cn from 'classnames';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/operator/throttleTime';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/merge';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/operator/distinctUntilChanged';

import { useMount } from 'hooks';
import DebugInfo from '../DebugInfo';
import SportEventSubscription from '../SportEventSubscription';

import './sportEventInViewSubscription.scss';

const {
    utils: { getDefaultVariables, isDebugEnabled },
    async: {
        constants: { SubscribeOrigin },
    },
} = core;

interface Props {
    matchId: string;
    version: string | undefined;
}

const onScroll$ = Observable.fromEvent(document, 'scroll');
const isDebugOn = isDebugEnabled();

const SportEventInViewSubscription: React.FC<Props> = ({
    matchId,
    version,
}) => {
    const variables = getSubscriptionVariables(matchId, version);

    const [inViewRef, isInView] = useInView();
    const [isSubscribed, setSubscribed] = useState<boolean>(isInView);

    const isScrolling = useRef<boolean>(false);
    const isInViewRef = useRef<boolean>(isInView);

    isInViewRef.current = isInView;

    useMount(() => {
        const scrollSub = onScroll$
            .throttleTime(100)
            .map(() => true)
            .merge(onScroll$.debounceTime(350).map(() => false))
            .distinctUntilChanged()
            .subscribe(scrolling => {
                isScrolling.current = scrolling;

                if (scrolling === false) {
                    // NOTE: Trigger subscription change when user stop scrolling
                    setSubscribed(isInViewRef.current);
                }
            });

        return () => {
            scrollSub.unsubscribe();
        };
    });

    useEffect(() => {
        // NOTE: Do not trigger subscription change when user is scrolling
        if (isScrolling.current) return;

        setSubscribed(isInView);
    }, [isInView]);

    return (
        <Fragment>
            <div
                ref={inViewRef}
                styleName="subscription-overlay"
                className="__app-SportEventInViewSubscription-subscription-overlay"
            />

            {isSubscribed && (
                <SportEventSubscription
                    variables={variables}
                    batch={true}
                    origin={SubscribeOrigin.MatchList}
                />
            )}

            {isDebugOn && (
                <SubscriptionRowDebugInfo
                    sportEventId={variables.sportEventId}
                    isSubscribed={isSubscribed}
                />
            )}
        </Fragment>
    );
};

interface SubscriptionRowDebugInfoProps {
    sportEventId: string;
    isSubscribed: boolean;
}

const SubscriptionRowDebugInfo = ({
    sportEventId,
    isSubscribed,
}: SubscriptionRowDebugInfoProps) => {
    return (
        <div styleName="subscription-debug ">
            <DebugInfo
                text={sportEventId}
                isInlineBlock={true}
                isRelativePosition={true}
            />
            <span
                styleName={cn('subscription-debug-status', {
                    'is-subscribed': isSubscribed,
                })}
            >
                {isSubscribed ? 'Subscribed' : 'Unsubscribed'}
            </span>
        </div>
    );
};

const getSubscriptionVariables = (
    matchId: string,
    version: string | undefined
):
    | GQL.OnUpdateSportEventSubscriptionVariables
    | GQL.SubscriptionOnUpdateSportEventArgs => {
    const subscriptionVariables: GQL.OnUpdateSportEventSubscriptionVariables = {
        sportEventId: matchId,
        isTopMarkets: true,
        version,
    };

    const { marketStatuses } = getDefaultVariables();

    if (marketStatuses) {
        return {
            ...subscriptionVariables,
            marketStatuses,
        };
    }

    return subscriptionVariables;
};

export default React.memo(SportEventInViewSubscription);
